<template>
  <aside id="offcanvas-sidemenu" class="app-sidebar -translate-x-full xl:translate-x-0" aria-label="Sidebar">
    <div id="logo-cont" class="app-sidebar-logo-container">
      <RouterLink :to="ROUTER.PARTNER.CUSTOMER.HOME_PAGE.PATH">
        <a href="javascript:void(0);" data-drawer-hide="offcanvas-sidemenu">
          <img src="../../assets/branch-logo.svg" class="app-sidebar-logo" alt="Digital Hands Logo" />
        </a>
      </RouterLink>
    </div>
    <div id="menu-cont" class="app-sidebar-menu-container flex flex-col gap-3">
      <RouterLink :to="ROUTER.PARTNER.LANDING_PAGE.PATH" class="button-text text-left !text-text !gap-1">
        <CaretLeftIcon class="size-4" />
        <span>Back to partner page</span>
      </RouterLink>
      <SideMenu side-menu-id="offcanvas-sidemenu" :items="menuItems" />
    </div>
    <div
      v-if="hasAccess(authUser, ROUTER.PARTNER.CUSTOMER.CONTACT_US_PAGE.accessPolicy)"
      id="cta-support-cont"
      class="flex absolute bottom-0 left-0 z-20 justify-center px-5 pt-3 pb-6 space-x-4 w-full dark:bg-container"
    >
      <div id="dropdown-cta" class="w-full rounded-lg" role="alert">
        <div class="flex mb-3 text-sm text-blue-800 dark:text-text">
          <LifeSaverIcon class="app-sidebar-menu-list-icon" />
          <span class="inline-flex items-center ml-2">Need support?</span>
          <div class="clear-both"></div>
        </div>
        <div class="text-center text-xs font-medium">
          <RouterLink
            :to="ROUTER.PARTNER.CUSTOMER.CONTACT_US_PAGE.PATH.replace(':tenantId', partnerSelectedTenant?.id)"
          >
            <a
              class="inline-flex items-center justify-center w-full py-2 px-5 text-sm font-medium focus:outline-none bg-green-700 rounded-lg border border-green-700"
              href="javascript:void(0);"
              data-drawer-hide="offcanvas-sidemenu"
            >
              <PhoneIcon class="app-sidebar-menu-list-icon !w-3 !h-3 mr-2" :viewBox="`0 0 12 12`" />
              <span>Contact us</span>
            </a>
          </RouterLink>
        </div>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { isEmpty } from 'lodash';
import { computed, markRaw, onMounted, onUnmounted, Raw, ref, watch } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import { initFlowbite } from 'flowbite';
import HomeIcon from '@/components/icons/HomeIcon.vue';
import ColumnIcon from '@/components/icons/ColumnIcon.vue';
import FileIcon from '@/components/icons/FileIcon.vue';
import InboxIcon from '@/components/icons/InboxIcon.vue';
import LinkIcon from '@/components/icons/LinkIcon.vue';
import GridIcon from '@/components/icons/GridIcon.vue';
import PhoneIcon from '@/components/icons/PhoneIcon.vue';
import LifeSaverIcon from '@/components/icons/LifeSaverIcon.vue';
import TicketIcon from '@/components/icons/TicketIcon.vue';
import UsersIcon from '@/components/icons/UsersIcon.vue';
import { useImpersonationInfo } from '@/hooks/useImpersonationInfo';
import { RouteItem, ROUTER } from '../../app-constants/router.constant';
import { SideMenuItem } from '@/entities/sideMenuItem.entity';
import SideMenu from './SideMenu.vue';
import { hasAccess, hasRelatedAccess } from '@/utils/user.utils';
import { ECisoModuleTabs } from '@/views/customer-dashboard-CISO/ciso.helpers';
import { EPromptAIModuleTabs } from '@/views/customer-dashboard-prompt-ai/promptAI.helpers';

import { usePartnerTenantStore } from '@/stores/partnerTenant.storage';
import CaretLeftIcon from '../icons/CaretLeftIcon.vue';
import useMultitenancy from '@/hooks/useMultitenancy';
import { useModuleStateStore } from '@/stores/moduleState.store';
import { useAccountInformation } from '@/hooks/useAccountInformation';

const router = useRouter();
const { tenant: partnerSelectedTenant } = usePartnerTenantStore();

const { params: routerParams } = router.currentRoute.value;
const currentTenantId = computed(() => (partnerSelectedTenant?.id ?? routerParams.tenantId) as string);

const impersonationInfo = useImpersonationInfo();
const { hasTechnologyChronicle, hasTechnologySentinelOne, hasProductEDR, hasProductSIEM } = useAccountInformation();
let authUser = impersonationInfo.impersonatedUser;

const {
  setSelectedTenant,
  selectedTenant,
  hasRelatedReadPermission,
  hasSelfReadPermission,

  isLoadingRelatedTenants,
  isRelatedTenantsFullyLoaded,

  fetchRelatedTenants,
  currentSelectedTenant,
  tenantDropdownOptions,
} = useMultitenancy({
  includeSoldProducts: true,
});

const { getTenantSelection } = useModuleStateStore();

const shouldShowOperationalMetrics = ref<boolean>(false);

watch(
  [
    (): unknown => currentSelectedTenant.value,
    (): unknown => selectedTenant.value,
    (): unknown => getTenantSelection(),
  ],
  () => {
    const seletectTenantId = getTenantSelection();

    const isSelfTenantCase = seletectTenantId
      ? seletectTenantId === authUser.tenantId
      : currentSelectedTenant.value.id === authUser.tenantId;

    if (isSelfTenantCase) {
      shouldShowOperationalMetrics.value =
        (hasTechnologyChronicle.value && hasProductSIEM.value) ||
        (hasTechnologySentinelOne.value && hasProductEDR.value);
    } else {
      const foundTenant = tenantDropdownOptions.value?.find((t) => t.id === seletectTenantId);
      if (!isEmpty(foundTenant)) {
        shouldShowOperationalMetrics.value =
          (foundTenant?.soldProducts?.hasChronicle && foundTenant?.soldProducts?.hasSiem) ||
          (foundTenant?.soldProducts?.hasSentinelone && foundTenant?.soldProducts?.hasEdr);
      }
    }
  },
);

// --- COMBINED MENU BUILDING ---
const menuItems = computed<SideMenuItem[]>(() => {
  const items: SideMenuItem[] = [];

  const getMenuLink = computed(
    () =>
      (link: string): string =>
        link.replace(':tenantId', currentTenantId.value),
  );

  // Helper function for adding menu items with access checks
  const addMenuItem = (
    route: RouteItem,
    icon?: Raw<object>,
    children?: SideMenuItem[],
    skipCheckRelated: boolean = false,
  ): void => {
    if (skipCheckRelated) {
      items.push({
        id: route.KEY,
        name: route.NAME,
        link: isEmpty(children) ? getMenuLink.value(route.PATH) : '',
        icon: icon ? markRaw(icon) : undefined,
        ...(children.length > 0 && { children }),
      });
      return;
    }

    if (hasRelatedAccess(authUser, route.accessPolicy)) {
      items.push({
        id: route.KEY,
        name: route.NAME,
        link: isEmpty(children) ? getMenuLink.value(route.PATH) : '',
        icon: icon ? markRaw(icon) : undefined,
        children: children,
      });
    }
  };

  // --- Build Main Menu Items ---
  addMenuItem(ROUTER.PARTNER.CUSTOMER.HOME_PAGE, HomeIcon, [], true);
  addMenuItem(ROUTER.PARTNER.CUSTOMER.MY_CASES_PAGE, TicketIcon);

  // --- Build Dashboard Items (with CISO Sub-items) ---
  const dashboardChildren: SideMenuItem[] = [];

  const OPERATION_METRIC_SIDEBAR_INDEX = 1;

  if (hasRelatedAccess(authUser, ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CISO_PAGE.accessPolicy)) {
    const cisoChildren: SideMenuItem[] = [
      {
        id: `${ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CISO_PAGE.KEY}-${ECisoModuleTabs.SecurityPosture}`,
        name: 'Security Posture',
        link: getMenuLink.value(
          `${ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CISO_PAGE.PATH}?tab=${ECisoModuleTabs.SecurityPosture}`,
        ),
      },

      {
        id: `${ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CISO_PAGE.KEY}-${ECisoModuleTabs.Compliance}`,
        name: 'Compliance',
        link: getMenuLink.value(
          `${ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CISO_PAGE.PATH}?tab=${ECisoModuleTabs.Compliance}`,
        ),
      },
    ];

    if (shouldShowOperationalMetrics.value) {
      cisoChildren.splice(OPERATION_METRIC_SIDEBAR_INDEX, 0, {
        id: `${ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CISO_PAGE.KEY}-${ECisoModuleTabs.OperationalMetric}`,
        name: 'Operational Metrics',
        link: getMenuLink.value(
          `${ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CISO_PAGE.PATH}?tab=${ECisoModuleTabs.OperationalMetric}`,
        ),
      });
    }

    dashboardChildren.push({
      id: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CISO_PAGE.KEY,
      name: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CISO_PAGE.NAME,
      children: cisoChildren,
      link: '',
    });
  }

  // Other dashboard sub-items (MITRE, SIEM, EDR, etc.)
  if (hasRelatedAccess(authUser, ROUTER.PARTNER.CUSTOMER.DASHBOARDS_MITRE_PAGE.accessPolicy)) {
    dashboardChildren.push({
      id: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_MITRE_PAGE.KEY,
      name: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_MITRE_PAGE.NAME,
      link: getMenuLink.value(ROUTER.PARTNER.CUSTOMER.DASHBOARDS_MITRE_PAGE.PATH),
    });
  }

  if (hasRelatedAccess(authUser, ROUTER.PARTNER.CUSTOMER.DASHBOARDS_SIEM_PAGE.accessPolicy)) {
    dashboardChildren.push({
      id: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_SIEM_PAGE.KEY,
      name: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_SIEM_PAGE.NAME,
      link: getMenuLink.value(ROUTER.PARTNER.CUSTOMER.DASHBOARDS_SIEM_PAGE.PATH),
    });
  }

  if (hasRelatedAccess(authUser, ROUTER.PARTNER.CUSTOMER.DASHBOARDS_EDR_PAGE.accessPolicy)) {
    dashboardChildren.push({
      id: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_EDR_PAGE.KEY,
      name: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_EDR_PAGE.NAME,
      link: getMenuLink.value(ROUTER.PARTNER.CUSTOMER.DASHBOARDS_EDR_PAGE.PATH),
    });
  }
  if (hasRelatedAccess(authUser, ROUTER.PARTNER.CUSTOMER.DASHBOARDS_EDR_CROWDSTRIKE_PAGE.accessPolicy)) {
    dashboardChildren.push({
      id: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_EDR_CROWDSTRIKE_PAGE.KEY,
      name: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_EDR_CROWDSTRIKE_PAGE.NAME,
      link: getMenuLink.value(ROUTER.PARTNER.CUSTOMER.DASHBOARDS_EDR_CROWDSTRIKE_PAGE.PATH),
    });
  }

  if (hasRelatedAccess(authUser, ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CASE_PAGE.accessPolicy)) {
    dashboardChildren.push({
      id: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CASE_PAGE.KEY,
      name: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CASE_PAGE.NAME,
      link: getMenuLink.value(ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CASE_PAGE.PATH),
    });
  }

  if (hasRelatedAccess(authUser, ROUTER.PARTNER.CUSTOMER.DASHBOARDS_VULNERABILITY_PAGE.accessPolicy)) {
    dashboardChildren.push({
      id: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_VULNERABILITY_PAGE.KEY,
      name: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_VULNERABILITY_PAGE.NAME,
      link: getMenuLink.value(ROUTER.PARTNER.CUSTOMER.DASHBOARDS_VULNERABILITY_PAGE.PATH),
    });
  }

  if (hasRelatedAccess(authUser, ROUTER.PARTNER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.accessPolicy)) {
    const promptAiPath = getMenuLink.value(ROUTER.PARTNER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.PATH);
    dashboardChildren.push({
      id: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.KEY,
      name: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.NAME,
      link: '',
      children: [
        {
          id: `${ROUTER.PARTNER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.KEY}-${EPromptAIModuleTabs.EmployeesUsage}`,
          name: 'Employees AI usage',
          link: `${promptAiPath}?tab=${EPromptAIModuleTabs.EmployeesUsage}`,
        },
        {
          id: `${ROUTER.PARTNER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.KEY}-${EPromptAIModuleTabs.ActivityLog}`,
          name: 'Activity log',
          link: `${promptAiPath}?tab=${EPromptAIModuleTabs.ActivityLog}`,
        },
      ],
    });
  }

  if (dashboardChildren.length > 0) {
    addMenuItem(ROUTER.PARTNER.CUSTOMER.DASHBOARDS_PAGE, ColumnIcon, dashboardChildren, true);
  }

  addMenuItem(ROUTER.PARTNER.CUSTOMER.FILES_PAGE, FileIcon);
  addMenuItem(ROUTER.PARTNER.CUSTOMER.ACTIVITIES_PAGE, InboxIcon);
  addMenuItem(ROUTER.PARTNER.CUSTOMER.SERVICE_PAGE, GridIcon);
  addMenuItem(ROUTER.PARTNER.CUSTOMER.MY_LINKS_PAGE, LinkIcon);
  addMenuItem(ROUTER.PARTNER.CUSTOMER.USER_MANAGEMENT_PAGE, UsersIcon);

  return items;
});

onMounted(() => {
  initFlowbite();
  resizeSideMenu();
  window.addEventListener('resize', resizeSideMenu);
});

onUnmounted(() => {
  window.removeEventListener('resize', resizeSideMenu);
});

const resizeSideMenu = (): void => {
  const offCanvasSideMenu = document.getElementById('offcanvas-sidemenu');
  const logoView = document.getElementById('logo-cont');
  const menuView = document.getElementById('menu-cont');
  const suppotCTAView = document.getElementById('cta-support-cont');
  const logoMargin = 24;
  if (!offCanvasSideMenu || !logoView || !menuView || !suppotCTAView) return;
  menuView.style.height =
    offCanvasSideMenu.offsetHeight - (logoView.offsetHeight + logoMargin + suppotCTAView.offsetHeight) + 'px';
};

watch(
  isLoadingRelatedTenants,
  () => {
    if (!hasRelatedReadPermission.value) {
      return;
    }

    if (!isLoadingRelatedTenants.value && isRelatedTenantsFullyLoaded.value) {
      setSelectedTenant();
    }
  },
  {
    immediate: true,
  },
);

onMounted(() => {
  if (hasSelfReadPermission.value && !hasRelatedReadPermission.value) {
    setSelectedTenant();
  }

  if (hasRelatedReadPermission.value) {
    fetchRelatedTenants();
  }
});
</script>
